/*
 * Timer Badge
 *
 * Each product card may render a `[data-timer-badge]` element that is `hidden`
 * by default and carries the scheduled window as NAIVE wall-clock strings with
 * no timezone designator (data-tb-start / data-tb-end, e.g.
 * "2026-06-15T10:00:00"). The merchant enters these values meaning EASTERN TIME
 * (America/New_York); Liquid emits the wall-clock numbers verbatim and this
 * script owns the timezone math. Because full-page caching makes Liquid's `now`
 * stale, visibility is decided here, client-side, against Date.now().
 *
 * Timezone: each boundary is interpreted as Eastern wall-clock time and
 * converted to an absolute UTC instant using America/New_York rules — DST-aware,
 * so it works in both EDT (UTC-4) and EST (UTC-5) with no fixed offset. The
 * resulting instants are compared to Date.now(), so the badge flips at the SAME
 * global moment for every visitor — anchored to Eastern Time, independent of the
 * visitor's local timezone AND of the shop's timezone setting.
 *
 *   - Inside the window  -> reveal the timer badge, mark its `.titan-badges`
 *                           wrapper active so the sibling discount badge hides.
 *   - Outside the window -> keep the timer badge hidden so the discount/sold-out
 *                           badge (if any) shows instead.
 *
 * Re-evaluated on DOMContentLoaded, on shopify:section:load (theme editor), and
 * on variant:update (the main product swaps its price container, re-rendering a
 * fresh, hidden timer badge that needs re-checking).
 *
 * A single interval re-checks every badge on each tick, so the badge appears the
 * moment its window opens and disappears the moment it closes — live, with no
 * page reload — and the countdown text (when enabled) updates each tick. The
 * interval runs only while at least one badge still has a future boundary to
 * cross (now < end) and stops once every badge has expired.
 */
(function () {
  'use strict';

  var BADGE_SELECTOR = '[data-timer-badge]';
  // Marker added to the badge's `.titan-badges` wrapper (present on both cards
  // and the main product) so CSS can hide the sibling discount/sold-out badge.
  var ACTIVE_CONTAINER_CLASS = 'timer-badge-active';
  // How often the badges are re-checked / the countdown is updated.
  var TICK_MS = 1000;
  // The merchant's wall-clock times are interpreted in this IANA zone.
  var SCHEDULE_TZ = 'America/New_York';
  var intervalId = null;

  // Milliseconds that `timeZone` is offset from UTC at the given absolute
  // instant. Positive east of UTC. Uses Intl so DST transitions are honored.
  function tzOffsetMs(instant, timeZone) {
    var dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    var p = dtf.formatToParts(instant).reduce(function (acc, part) {
      acc[part.type] = part.value;
      return acc;
    }, {});
    // Intl can format hour "24" at midnight; normalize so Date.UTC is correct.
    var hour = p.hour === '24' ? 0 : Number(p.hour);
    var asUTC = Date.UTC(
      Number(p.year),
      Number(p.month) - 1,
      Number(p.day),
      hour,
      Number(p.minute),
      Number(p.second)
    );
    return asUTC - instant.getTime();
  }

  // Treat a naive wall-clock string (no timezone) as Eastern Time and return the
  // absolute Date (UTC instant) it denotes, DST-aware. Returns null if unparseable.
  function easternWallClockToInstant(naive) {
    if (!naive) return null;
    // First read the numbers as if they were UTC...
    var guess = new Date(naive + 'Z');
    if (isNaN(guess.getTime())) return null;
    // ...then shift by Eastern's real offset at that instant (handles EST/EDT).
    var offset = tzOffsetMs(guess, SCHEDULE_TZ);
    var instant = new Date(guess.getTime() - offset);
    // Re-measure the offset AT the resulting instant and correct once. The first
    // guess can sit on the wrong side of a DST transition (e.g. a wall-clock time
    // in the hour right after "spring forward"), which the refinement resolves.
    var refinedOffset = tzOffsetMs(instant, SCHEDULE_TZ);
    if (refinedOffset !== offset) {
      instant = new Date(guess.getTime() - refinedOffset);
    }
    return instant;
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function formatRemaining(remainingMs) {
    var totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    var days = Math.floor(totalSeconds / 86400);
    var hours = Math.floor((totalSeconds % 86400) / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;

    if (days > 0) {
      return days + 'd ' + pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);
    }
    return pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);
  }

  function applyCountdown(badge, remainingMs) {
    var textEl = badge.querySelector('[data-timer-badge-text]');
    var label = badge.getAttribute('data-label') || '';
    var remaining = formatRemaining(remainingMs);

    if (textEl) {
      textEl.textContent = label ? label + ' · ' + remaining : remaining;
    }
    // Give screen readers the offer context plus the remaining time.
    badge.setAttribute('aria-label', (label ? label + ' — ' : '') + remaining + ' left');
  }

  function evaluate() {
    var now = Date.now();
    var badges = document.querySelectorAll(BADGE_SELECTOR);
    // Keep ticking while any badge still has a future boundary to cross (it has
    // not started yet, or is mid-window) so the badge appears/disappears live.
    var keepTicking = false;

    Array.prototype.forEach.call(badges, function (badge) {
      // Boundaries are naive Eastern wall-clock strings; convert each to an
      // absolute UTC instant using America/New_York rules (DST-aware) so the
      // comparison is independent of the visitor's and shop's timezone.
      var startInstant = easternWallClockToInstant(badge.getAttribute('data-tb-start'));
      var endInstant = easternWallClockToInstant(badge.getAttribute('data-tb-end'));
      var container = badge.closest('[data-titan-badges]');

      var valid = startInstant !== null && endInstant !== null;
      var start = valid ? startInstant.getTime() : NaN;
      var end = valid ? endInstant.getTime() : NaN;
      var withinWindow = valid && now >= start && now < end;

      // Anything not yet expired needs continued re-checking.
      if (valid && now < end) keepTicking = true;

      if (withinWindow) {
        badge.hidden = false;
        if (container) container.classList.add(ACTIVE_CONTAINER_CLASS);

        if (badge.getAttribute('data-countdown') === 'true') {
          applyCountdown(badge, end - now);
        }
      } else {
        badge.hidden = true;
        if (container) container.classList.remove(ACTIVE_CONTAINER_CLASS);
      }
    });

    // Run the interval continuously while any badge is live or pending; stop once
    // every badge has expired (or none are present) so we don't tick forever.
    if (keepTicking && intervalId === null) {
      intervalId = window.setInterval(evaluate, TICK_MS);
    } else if (!keepTicking && intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', evaluate);
  } else {
    evaluate();
  }

  // Re-evaluate when a section is re-rendered (theme editor, AJAX paginate, etc.).
  document.addEventListener('shopify:section:load', evaluate);

  // On the main product, a variant change replaces the price container with
  // freshly rendered (hidden) markup. Re-evaluate after that swap has run.
  document.addEventListener('variant:update', function () {
    window.setTimeout(evaluate, 0);
  });
})();
